import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, MessageSquare, Zap, ChevronRight, BarChart3, FileText, LayoutTemplate, Layers, Search as SearchIcon, CheckCircle, Globe, BookOpen, Calculator, Share2, ShieldCheck, ArrowRight, Cpu } from 'lucide-react';

export const LandingPage: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            const element = document.getElementById(id);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location]);


    return (
        <div className="min-h-screen bg-app-bg font-sans text-white overflow-x-hidden selection:bg-primary/30">
            {/* Ambient Background Elements */}
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px] -z-10 animate-pulse-slow"></div>
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-info/5 rounded-full blur-[120px] -z-10"></div>
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none opacity-20"></div>

            {/* Navigation */}
            <nav className="fixed w-full bg-black/40 backdrop-blur-xl z-50 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-20 items-center">
                        <Link to="/" className="flex items-center gap-2 shrink-0 group">
                            <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-10 sm:h-12 w-auto object-contain drop-shadow-[0_0_15px_rgba(255,107,0,0.3)] group-hover:scale-105 transition-transform duration-300" />
                        </Link>
                        <div className="hidden md:flex items-center space-x-10">
                            <a href="#features" className="text-xs font-bold text-text-secondary hover:text-primary transition-colors">Features</a>
                            <a href="#how-it-works" className="text-xs font-bold text-text-secondary hover:text-primary transition-colors">How it Works</a>
                            <a href="#pricing" className="text-xs font-bold text-text-secondary hover:text-primary transition-colors">Pricing</a>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <Link to="/login" className="text-sm font-bold text-white hover:text-primary transition-colors mr-4">
                                Log In
                            </Link>
                            <Link to="/signup" className="px-6 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:bg-orange-600 transition-all shadow-[0_0_20px_rgba(255,107,0,0.3)] transform hover:-translate-y-0.5 whitespace-nowrap">
                                Get Started
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-48 pb-32 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div className="z-10">
                            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/5 border border-primary/20 text-primary text-xs font-bold mb-8 shadow-[0_0_20px_rgba(255,107,0,0.1)]">
                                <span className="flex h-2 w-2 rounded-full bg-primary mr-3 animate-pulse"></span>
                                AI Product Architecture Platform
                            </div>
                            <h1 className="text-5xl lg:text-7xl font-bold font-heading leading-[1.05] mb-8 tracking-tight">
                                Your <span className="bg-gradient-to-r from-primary via-orange-400 to-amber-300 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]">AI Co-Pilot</span>
                                <br /> for Product Strategy & Execution
                            </h1>
                            <p className="text-lg text-text-secondary mb-12 leading-relaxed max-w-lg font-medium">
                                DocSprintAI transforms product ideas into product documentation, market intelligence, competitor insights, and engineering estimates, enabling teams to move from concept to launch with clarity.
                            </p>


                            <div className="flex items-center gap-10">
                                <div className="flex -space-x-3">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="w-11 h-11 rounded-2xl bg-card border border-white/10 overflow-hidden shadow-2xl">
                                            <img src={`https://i.pravatar.cc/100?img=${i+10}`} alt="User" className="grayscale hover:grayscale-0 transition-all duration-500" />
                                        </div>
                                    ))}
                                </div>
                                <div className="h-10 w-[1px] bg-white/10 hidden sm:block"></div>
                                <div>
                                    <div className="flex items-center gap-1 text-primary">
                                        {'★'.repeat(5)}
                                    </div>
                                    <p className="text-xs font-bold text-white mt-1"><span className="text-primary">240+</span> Happy Product Managers</p>
                                </div>
                            </div>
                        </div>

                        {/* Hero Visual Mockup */}
                        <div className="relative">
                            <div className="absolute -inset-20 bg-primary/10 blur-[100px] -z-10 rounded-full animate-pulse"></div>
                            <div className="relative rounded-[2.5rem] bg-[#131B2C]/40 backdrop-blur-3xl shadow-[0_50px_100px_rgba(0,0,0,0.5)] border border-white/10 p-8 transform group hover:rotate-1 transition-all duration-700">
                                {/* Floating Elements */}
                                <div className="absolute -left-12 top-14 p-5 bg-[#131B2C] rounded-2xl shadow-2xl border border-white/10 animate-float">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center text-primary shadow-inner border border-primary/20">
                                            <Sparkles size={24} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-text-secondary font-bold uppercase tracking-wider">AI Agent</div>
                                            <div className="text-xl font-bold text-white tracking-tight">Processing...</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="absolute -right-10 bottom-24 p-5 bg-[#131B2C] rounded-2xl shadow-2xl border border-white/10 animate-float-delayed">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-info/20 flex items-center justify-center text-info shadow-inner border border-info/20">
                                            <CheckCircle size={24} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-text-secondary font-bold uppercase tracking-wider">Status</div>
                                            <div className="text-xl font-bold text-white tracking-tight">Complete</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Main Card Content */}
                                <div className="space-y-8">
                                    {/* Mock Header */}
                                    <div className="flex items-center justify-between border-b border-white/5 pb-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-text-secondary border border-white/10 shadow-inner">
                                                <LayoutTemplate size={24} />
                                            </div>
                                            <div>
                                                <div className="h-5 w-40 bg-white/10 rounded-full animate-pulse mb-2"></div>
                                                <div className="h-3 w-24 bg-white/5 rounded-full"></div>
                                            </div>
                                        </div>
                                        <div className="px-4 py-1.5 rounded-full bg-info/10 text-info text-[10px] font-bold uppercase tracking-widest border border-info/20">
                                            Active
                                        </div>
                                    </div>

                                    {/* Document List */}
                                    <div className="space-y-4">
                                        {[
                                            { icon: FileText, name: 'Product_Requirements.pdf', time: '2m ago', color: 'bg-primary/20 text-primary border-primary/20' },
                                            { icon: BookOpen, name: 'User_Stories.csv', time: '1m ago', color: 'bg-info/20 text-info border-info/20' },
                                            { icon: BarChart3, name: 'Cost_Estimate.xlsx', time: 'Just now', color: 'bg-purple-500/20 text-purple-400 border-purple-500/20' }
                                        ].map((doc, idx) => (
                                            <div key={idx} className="flex items-center p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/20 transition-all cursor-default group/doc">
                                                <div className={`w-12 h-12 rounded-xl ${doc.color} flex items-center justify-center mr-4 shadow-lg border`}>
                                                    <doc.icon size={20} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-bold text-white text-sm tracking-tight">{doc.name}</div>
                                                    <div className="text-xs font-semibold text-text-secondary opacity-80">{doc.time}</div>
                                                </div>
                                                <div className="text-white/20 group-hover/doc:text-primary transition-colors">
                                                    <ChevronRight size={20} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Action Button Mock */}
                                    <div className="mt-6 pt-2">
                                        <div className="w-full h-14 bg-white text-black rounded-2xl flex items-center justify-center font-bold text-sm shadow-2xl hover:scale-[1.02] transition-transform">
                                            Export All Documents
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* How It Works Section */}
            <section id="how-it-works" className="py-32 bg-transparent relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="text-primary font-bold tracking-wider uppercase text-xs">Workflow</span>
                        <h2 className="text-4xl md:text-5xl font-bold font-heading mt-4 mb-6 text-white tracking-tight">From Idea to <span className="text-primary drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]">Execution</span> in Minutes</h2>
                        <p className="text-lg text-text-secondary font-medium">Stop spending weeks on documentation. Let DocSprintAI handle the heavy lifting while you focus on building.</p>
                    </div>

                    <div className="relative">
                        <div className="grid md:grid-cols-3 gap-8 relative z-10 text-left">
                            {[
                                { step: "01", title: "Input Idea", desc: "Describe your product vision, target audience, and core features in simple text.", icon: Sparkles },
                                { step: "02", title: "AI Analysis", desc: "Our agents assist with research, identifying NFRs and UIRs automatically.", icon: SearchIcon },
                                { step: "03", title: "Generate Docs", desc: "Get comprehensive BRD, PRD, and User Stories generated instantly.", icon: FileText },
                                { step: "04", title: "Export Tasks", desc: "Convert stories into actionable tasks ready for your development sprint.", icon: CheckCircle },
                                { step: "05", title: "Estimation", desc: "Get instant cost and timeline estimates based on complexity.", icon: Calculator },
                                { step: "06", title: "Integrations", desc: "Export user stories, tasks, and test cases to Jira, Asana, ADO, and GitHub Projects.", icon: Share2 }
                            ].map((item, i) => (
                                <div key={i} className="group relative bg-[#131B2C]/40 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/5 hover:border-white/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden shadow-2xl">
                                    <div className="absolute top-0 right-0 p-8 text-7xl font-bold text-white/5 group-hover:text-primary/10 transition-colors pointer-events-none">{item.step}</div>
                                    <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-8 border border-primary/20 shadow-inner group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-500">
                                        <item.icon size={24} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-4 tracking-tight">{item.title}</h3>
                                    <p className="text-text-secondary text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section id="features" className="py-32 bg-transparent relative border-y border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="text-primary font-bold tracking-wider uppercase text-xs">Capabilities</span>
                        <h2 className="text-4xl md:text-5xl font-bold font-heading mt-4 mb-6 text-white tracking-tight">Built for <span className="bg-gradient-to-r from-info to-blue-400 bg-clip-text text-transparent">Modern Product Teams</span></h2>
                        <p className="text-lg text-text-secondary font-medium">Comprehensive tools to streamline your entire product planning lifecycle.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                title: "Intelligence Suite",
                                desc: "Automated market scanning, competitor identification, and SWOT synthesis.",
                                color: "from-orange-500/20 to-red-500/20",
                                icon: BarChart3,
                                accent: "bg-primary"
                            },
                            {
                                title: "Full-Stack Specs",
                                desc: "High-density BRD, PRD, and technical requirements documentation.",
                                color: "from-info/20 to-blue-500/20",
                                icon: Layers,
                                accent: "bg-info"
                            },
                            {
                                title: "Sprint Orchestration",
                                desc: "Native user stories with full acceptance criteria and task breakdowns.",
                                color: "from-green-500/20 to-emerald-500/20",
                                icon: Zap,
                                accent: "bg-green-500"
                            }
                        ].map((item, i) => (
                            <div key={i} className="group relative p-10 rounded-[2.5rem] bg-[#131B2C]/30 backdrop-blur-xl border border-white/5 hover:border-white/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden shadow-2xl">
                                <div className={`absolute top-0 right-0 w-48 h-48 bg-gradient-to-br ${item.color} rounded-full blur-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-700`}></div>
                                <div className={`w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-8 group-hover:${item.accent} transition-colors duration-500 shadow-inner`}>
                                    <item.icon size={32} className="text-white/40 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-2xl font-bold mb-4 font-heading text-white tracking-tight">{item.title}</h3>
                                <p className="text-text-secondary leading-relaxed mb-8 font-medium">{item.desc}</p>
                                <a href="#" className="inline-flex items-center text-sm font-bold text-primary group-hover:translate-x-2 transition-transform">
                                    Learn More <ChevronRight size={14} className="ml-2" />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product Intelligence Highlight Section */}
            <section className="py-32 bg-transparent text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -z-0"></div>
                <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-info/10 rounded-full blur-[100px] -z-0"></div>
                
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div>
                            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider mb-8">
                                <Sparkles size={16} className="mr-3" />
                                New Feature
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-bold font-heading mb-8 leading-tight text-white tracking-tight">
                                Transform Your Strategy with <br /><span className="text-primary drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]">Market Intelligence</span>
                            </h2>
                            <p className="text-lg text-text-secondary mb-12 leading-relaxed font-medium">
                                Our AI Agents go beyond documentation. They act as your market research team, providing deep insights to validate your product vision.
                            </p>
                            
                            <ul className="space-y-6 mb-12">
                                {[
                                    { title: "Competitor Discovery", desc: "Identify key players and their market positioning automatically." },
                                    { title: "Market Trend Analysis", desc: "Stay ahead with real-time analysis of industry shifts and opportunities." },
                                    { title: "Automated SWOT", desc: "Comprehensive Strengths, Weaknesses, Opportunities, and Threats mapping." },
                                    { title: "Pitch Deck Generation", desc: "Instantly create professional slides to communicate your product value." }
                                ].map((feature, i) => (
                                    <li key={i} className="flex gap-5 group/item">
                                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-1 border border-primary/20 group-hover/item:bg-primary group-hover/item:text-white transition-all">
                                            <CheckCircle size={14} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white text-sm tracking-tight">{feature.title}</h4>
                                            <p className="text-text-secondary text-sm font-medium">{feature.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            
                            <Link to="/signup" className="px-8 py-4 rounded-xl bg-primary text-white font-bold text-sm tracking-wide hover:bg-orange-600 transition-all shadow-[0_0_30px_rgba(255,107,0,0.3)] inline-flex items-center group">
                                Explore Intelligence Suite <ChevronRight size={18} className="ml-3 group-hover:translate-x-2 transition-transform" />
                            </Link>
                        </div>
                        
                        <div className="relative">
                            <div className="rounded-[2.5rem] border border-white/5 bg-[#131B2C]/50 backdrop-blur-3xl p-10 shadow-[0_40px_80px_rgba(0,0,0,0.5)] relative overflow-hidden group">
                                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/20 blur-[60px] rounded-full group-hover:scale-150 transition-transform duration-700"></div>
                                <div className="absolute -top-6 -right-6 p-5 bg-primary rounded-2xl shadow-2xl animate-float">
                                    <BarChart3 size={32} className="text-white" />
                                </div>
                                <img 
                                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800" 
                                    alt="Intelligence Dashboard" 
                                    className="rounded-3xl shadow-2xl border border-white/10 opacity-60 grayscale group-hover:grayscale-0 transition-all duration-700"
                                />
                                <div className="mt-10 space-y-6">
                                    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5">
                                        <div className="h-full w-[70%] bg-primary shadow-[0_0_15px_rgba(255,107,0,1)] animate-pulse"></div>
                                    </div>
                                    <div className="flex justify-between text-xs font-bold text-white tracking-wide">
                                        <span className="flex items-center gap-2"><div className="w-1 h-1 bg-primary rounded-full animate-ping"></div> Scanning Market Data...</span>
                                        <span className="text-primary">70%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-32 bg-transparent relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <span className="text-primary font-bold tracking-wider uppercase text-xs">Pricing Plans</span>
                        <h2 className="text-4xl md:text-5xl font-bold font-heading mt-4 mb-6 text-white tracking-tight">Select Your <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]">Growth Path</span></h2>
                        <p className="text-lg text-text-secondary font-medium">Select the perfect plan to accelerate your product development requirements.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-10 items-stretch">
                                {/* Free Plan */}
                        <div className="flex flex-col p-8 rounded-[2.5rem] bg-[#131B2C]/30 backdrop-blur-xl border border-white/5 shadow-2xl hover:border-white/10 transition-all duration-500 hover:-translate-y-2 relative group overflow-hidden h-full">
                            <div className="absolute top-0 left-0 w-full h-1 bg-white/5 group-hover:bg-white/20 transition-colors"></div>
                            <div className="mb-8">
                                <div className="inline-flex px-3 py-1 rounded-full bg-white/5 text-text-secondary text-[9px] font-black uppercase tracking-widest mb-4 border border-white/5">Default</div>
                                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">Explorer</h3>
                                <p className="text-text-secondary text-sm">Entry level plan.</p>
                            </div>
                            <div className="flex items-baseline mb-10">
                                <span className="text-5xl font-bold text-white tracking-tight">$0</span>
                                <span className="text-text-secondary ml-3 text-sm font-medium opacity-80">/ Lifetime</span>
                            </div>

                            <div className="space-y-4 mb-10 flex-1 text-left">
                                {[
                                    { text: "100K Generative Tokens", icon: Cpu },
                                    { text: "1 Active Project", icon: Layers },
                                    { text: "Standard AI Orchestration", icon: BookOpen }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-4 text-sm font-medium text-text-secondary text-left">
                                        <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center shrink-0 border border-white/5 shadow-inner">
                                            <item.icon size={16} />
                                        </div>
                                        <span>{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            <Link to="/signup" className="block w-full py-4 rounded-xl bg-white/5 text-white font-bold tracking-wide hover:bg-white/10 transition-all text-center border border-white/5">
                                Activate System
                            </Link>
                        </div>

                        {/* Starter Plan - POPULAR (Growth) */}
                        <div className="flex flex-col p-10 rounded-[2.5rem] bg-[#161F33] text-white shadow-[0_0_50px_rgba(16,185,129,0.15)] relative transform md:-translate-y-6 z-10 border border-emerald-500/30 h-full scale-105">
                            <div className="absolute -inset-[1px] bg-gradient-to-br from-emerald-500 to-teal-500 rounded-[2.55rem] -z-10 opacity-30 blur-[2px]"></div>
                            <div className="absolute top-0 right-0">
                                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold px-5 py-2 rounded-bl-2xl rounded-tr-[2.4rem] uppercase tracking-wider shadow-2xl flex items-center gap-2">
                                     Popular Choice
                                </div>
                            </div>

                            <div className="mb-8">
                                <div className="inline-flex px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-black uppercase tracking-widest mb-4 border border-emerald-500/20">Advanced</div>
                                <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-3 tracking-tight">
                                    Growth <LayoutTemplate size={18} className="text-emerald-400" />
                                </h3>
                                <p className="text-text-secondary text-sm opacity-80">High-performance tier.</p>
                            </div>
                            <div className="flex items-baseline mb-10">
                                <span className="text-6xl font-bold text-white tracking-tight">₹4,999</span>
                                <span className="text-emerald-400/60 ml-3 text-sm font-medium">/ 1M Tokens</span>
                            </div>

                            <div className="space-y-4 mb-12 flex-1">
                                {[
                                    { text: "1M Generative Tokens", icon: Cpu },
                                    { text: "3 Concurrent Projects", icon: Layers },
                                    { text: "Full AI Intelligence Suite", icon: Sparkles },
                                    { text: "Structure Visualizer", icon: LayoutTemplate }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-4 text-sm font-medium text-white group-hover:text-emerald-400 transition-colors text-left">
                                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0 shadow-inner">
                                            <item.icon size={18} />
                                        </div>
                                        <span>{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            <Link to="/signup" className="block w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold tracking-wide hover:brightness-110 shadow-[0_20px_40px_rgba(16,185,129,0.3)] transition-all text-center">
                                Provision Growth
                            </Link>
                        </div>

                        {/* Intelligence Plan */}
                        <div className="flex flex-col p-8 rounded-[2.5rem] bg-[#131B2C]/30 backdrop-blur-xl border border-primary/20 shadow-2xl hover:border-primary/40 transition-all duration-500 hover:-translate-y-2 relative group overflow-hidden h-full">
                            <div className="absolute top-0 left-0 w-full h-1 bg-primary/20 group-hover:bg-primary/50 transition-colors"></div>
                            <div className="mb-8 text-left">
                                <div className="inline-flex px-3 py-1 rounded-full bg-primary/20 text-primary text-[9px] font-black uppercase tracking-widest mb-4 border border-primary/30 shadow-[0_0_15px_rgba(255,107,0,0.1)]">Elite Integration</div>
                                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">Intelligence</h3>
                                <p className="text-text-secondary text-sm opacity-80">Full stack AI enterprise.</p>
                            </div>
                            <div className="flex items-baseline mb-10 text-white tracking-tight">
                                <span className="text-5xl font-bold">₹9,999</span>
                                <span className="text-primary/60 ml-3 text-sm font-medium">/ 5M Tokens</span>
                            </div>

                            <div className="space-y-4 mb-10 flex-1 text-left">
                                {[
                                    { text: "5M Generative Tokens", icon: Cpu },
                                    { text: "Unlimited Projects", icon: Layers },
                                    { text: "Elite Product Analysis", icon: BarChart3 },
                                    { text: 'Market Velocity Scanning', icon: SearchIcon },
                                    { text: 'Enterprise Deck Synthesis', icon: FileText },
                                    { text: 'Priority Support', icon: CheckCircle },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-4 text-sm font-medium text-text-secondary text-left">
                                        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0 border border-primary/20 shadow-inner">
                                            <item.icon size={16} />
                                        </div>
                                        <span>{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            <Link to="/signup" className="block w-full py-4 rounded-xl bg-primary/10 border border-primary/30 text-primary font-bold tracking-wide hover:bg-primary/20 transition-all text-center shadow-[0_0_30px_rgba(255,107,0,0.1)]">
                                Initialize Elite
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
            {/* CTA Section */}
            <section className="py-32 bg-transparent relative overflow-hidden border-t border-white/5">
                <div className="absolute inset-0 bg-primary/5 -z-10"></div>
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] -translate-y-1/2"></div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold font-heading text-white mb-6 tracking-tight leading-tight">
                        Ready to Supercharge Your <br /><span className="text-primary">Product Velocity?</span>
                    </h2>
                    <p className="text-xl text-text-secondary mb-10 max-w-2xl mx-auto font-light">
                        Join thousands of product teams using DocSprintAI to automate their requirements and ship faster.
                    </p>
                    <Link to="/signup" className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 rounded-full bg-white text-black font-bold text-lg hover:bg-orange-50 transition-all shadow-[0_20px_40px_rgba(255,107,0,0.2)] hover:-translate-y-1 group">
                        Get Started <ArrowRight size={20} className="ml-3 group-hover:translate-x-2 transition-transform" />
                    </Link>
                </div>
            </section>

            {/* Dark Footer */}
            <footer className="bg-[#131B2C]/50 backdrop-blur-3xl text-white pt-24 pb-12 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-16 mb-20">
                        <div className="col-span-1 md:col-span-1">
                            <Link to="/" className="flex items-center gap-3 mb-8 group">
                                <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-10 w-auto object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300" />
                            </Link>
                            <p className="text-text-secondary leading-relaxed text-sm font-medium">
                                The AI architecture platform that automates product strategy and documentation.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold text-sm mb-6 text-white">Quick Links</h4>
                            <ul className="space-y-4 text-sm font-medium text-text-secondary">
                                <li><a href="#features" className="hover:text-primary transition-colors">Features</a></li>
                                <li><a href="#pricing" className="hover:text-primary transition-colors">Pricing</a></li>
                                <li><Link to="/login" className="hover:text-primary transition-colors">Log In</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-sm mb-6 text-white">Legal</h4>
                            <ul className="space-y-4 text-sm font-medium text-text-secondary">
                                <li><Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link></li>
                                <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-sm mb-6 text-white">Contact</h4>
                            <ul className="space-y-4 text-sm font-medium text-text-secondary">
                                <li><a href="mailto:sales@baigenlabs.com" className="hover:text-primary transition-colors lowercase">sales@baigenlabs.com</a></li>
                                <li><a href="https://www.linkedin.com/company/baigen-technolabs" className="hover:text-primary transition-colors">LinkedIn</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-white/5 pt-10 flex flex-col md:flex-row justify-center items-center gap-4">
                        <p className="text-text-secondary text-sm opacity-60">© 2026 Baigen TechnoLabs. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};
